import type { HelpTopic } from './types';
import { helpTopics } from './helpTopics';

/** Keyword synonyms — maps common search terms to canonical keywords. */
const synonyms: Record<string, string> = {
  missing: 'not accounted',
  unaccounted: 'not accounted',
  checkpoint: 'review',
  offline: 'sync',
  tenant: 'tenant',
  badge: 'scope',
  permissions: 'roles',
  permission: 'roles',
  accounted: 'accounted',
};

function expandQuery(query: string): string {
  const lower = query.toLowerCase();
  const expanded = synonyms[lower];
  return expanded ? `${lower} ${expanded}` : lower;
}

function matchScore(topic: HelpTopic, query: string): number {
  const q = expandQuery(query);
  const terms = q.split(/\s+/).filter(Boolean);
  let score = 0;

  for (const term of terms) {
    const t = term.toLowerCase();
    if (topic.title.toLowerCase().includes(t)) score += 10;
    if (topic.summary.toLowerCase().includes(t)) score += 5;
    if (topic.keywords.some((k) => k.toLowerCase().includes(t))) score += 8;
    if (topic.audience.some((a) => a.toLowerCase().includes(t))) score += 3;
    if (topic.relatedRoutes?.some((r) => r.toLowerCase().includes(t))) score += 2;
    // Search section titles and text body
    for (const section of topic.sections) {
      if (section.title.toLowerCase().includes(t)) score += 4;
      // For ReactNode body, convert to string if possible
      if (typeof section.body === 'string' && section.body.toLowerCase().includes(t)) score += 2;
    }
  }

  return score;
}

export function searchHelpTopics(query: string): HelpTopic[] {
  if (!query.trim()) return helpTopics;

  const scored = helpTopics
    .map((topic) => ({ topic, score: matchScore(topic, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.map(({ topic }) => topic);
}

export function getHelpTopicBySlug(slug: string): HelpTopic | undefined {
  return helpTopics.find((t) => t.slug === slug);
}
